import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { AdminGuard, AdminNav } from "../components/admin/AdminShell";
import { formatPrice } from "../lib/format";
import { Package, ArrowRight, Truck, Warning, ShieldWarning, MapPinLine, EnvelopeOpen, Copy } from "@phosphor-icons/react";

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "paid", label: "Paid — Ready to Ship" },
  { key: "label_purchased", label: "Label Purchased" },
  { key: "refunded", label: "Refunded" },
  { key: "partially_refunded", label: "Partial Refund" },
];

function StatusBadge({ status }) {
  const styleMap = {
    paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
    label_purchased: "bg-brand-accent/10 text-brand-navy border-brand-accent/40",
    refunded: "bg-red-50 text-red-700 border-red-200",
    partially_refunded: "bg-amber-50 text-amber-800 border-amber-200",
  };
  return (
    <span
      data-testid={`order-status-${status}`}
      className={`inline-flex items-center px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] rounded-full border ${
        styleMap[status] || "bg-brand-surface text-brand-muted border-brand-border"
      }`}
    >
      {(status || "unknown").replace(/_/g, " ")}
    </span>
  );
}

export default function AdminOrders() {
  const [status, setStatus] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [config, setConfig] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);

  const load = async (s) => {
    setLoading(true);
    try {
      const params = s ? { status: s, limit: 100 } : { limit: 100 };
      const { data } = await api.get("/admin/orders", { params });
      setOrders(data.orders || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const loadHealth = async () => {
    setHealthLoading(true);
    try {
      const [reconcile, cfg] = await Promise.all([
        api.get("/admin/orders/reconcile", { params: { limit: 100 } }),
        api.get("/admin/orders/config"),
      ]);
      setHealth(reconcile.data);
      setConfig(cfg.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load fulfillment health");
      setHealth(null);
    } finally {
      setHealthLoading(false);
    }
  };

  useEffect(() => {
    load(status);
  }, [status]);

  useEffect(() => {
    loadHealth();
  }, []);

  return (
    <AdminGuard>
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 py-10" data-testid="admin-orders-page">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="overline mb-2">Fulfillment</div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-brand-navy">
              Orders
            </h1>
            <p className="mt-2 text-brand-muted text-sm max-w-2xl">
              Every paid order routes through Shippo for signature-required delivery.
              Purchase a label from the order detail once you have verified the shipping address.
            </p>
          </div>
        </div>

        <FulfillmentHealthPanel
          health={health}
          config={config}
          loading={healthLoading}
          onRefresh={loadHealth}
        />

        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="admin-orders-tabs">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key || "all"}
              onClick={() => setStatus(t.key)}
              data-testid={`admin-orders-tab-${t.key || "all"}`}
              className={
                "px-4 py-2 text-xs font-mono uppercase tracking-widest border rounded-full transition-colors " +
                (status === t.key
                  ? "bg-brand-navy text-white border-brand-navy"
                  : "bg-white text-brand-navy border-brand-border hover:border-brand-navy")
              }
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mps-card p-10 text-brand-muted" data-testid="admin-orders-loading">
            Loading orders…
          </div>
        ) : orders.length === 0 ? (
          <div className="mps-card p-16 text-center" data-testid="admin-orders-empty">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-brand-surface border border-brand-border mb-4">
              <Package size={20} weight="regular" className="text-brand-muted" />
            </div>
            <div className="font-display text-xl font-semibold text-brand-navy">
              No orders match this filter.
            </div>
          </div>
        ) : (
          <div className="mps-card p-0 overflow-hidden" data-testid="admin-orders-list">
            <div className="hidden md:grid grid-cols-12 gap-3 px-6 py-3 bg-brand-surface border-b border-brand-border text-[10px] font-semibold uppercase tracking-[0.14em] text-brand-muted">
              <div className="col-span-3">Order · Customer</div>
              <div className="col-span-2">Placed</div>
              <div className="col-span-2">Items</div>
              <div className="col-span-2">Total</div>
              <div className="col-span-2">Fulfillment</div>
              <div className="col-span-1 text-right">Action</div>
            </div>
            <div className="divide-y divide-brand-border">
              {orders.map((o) => {
                const shippo = o.shippo || {};
                const items = o.items || [];
                const trackingStatus = shippo.tracking_status;
                const labelPurchased = Boolean(shippo.shippo_transaction_id);
                return (
                  <div
                    key={o.id || o.session_id}
                    data-testid={`admin-order-row-${o.id || o.session_id}`}
                    className="grid grid-cols-1 md:grid-cols-12 gap-3 px-6 py-5 items-center hover:bg-brand-surface/60 transition-colors"
                  >
                    <div className="md:col-span-3">
                      <div className="font-mono text-xs text-brand-muted">
                        {(o.id || "").slice(0, 12)}…
                      </div>
                      <div className="font-semibold text-brand-navy">
                        {o.customer_email || "—"}
                      </div>
                      <div className="mt-1"><StatusBadge status={o.status} /></div>
                    </div>
                    <div className="md:col-span-2 text-sm text-brand-muted font-mono">
                      {(o.created_at || "").slice(0, 10)}
                    </div>
                    <div className="md:col-span-2 text-sm text-brand-text">
                      <div>{items.length} SKU{items.length === 1 ? "" : "s"}</div>
                      <div className="text-[11px] text-brand-muted">
                        {items.reduce((n, it) => n + (it.quantity || 0), 0)} vials
                      </div>
                    </div>
                    <div className="md:col-span-2 font-semibold text-brand-navy">
                      {formatPrice(o.total_cents || 0)}
                    </div>
                    <div className="md:col-span-2 text-xs text-brand-muted">
                      {labelPurchased ? (
                        <div>
                          <div className="text-brand-navy font-semibold">
                            {shippo.carrier || "Shippo"} · {shippo.service || ""}
                          </div>
                          <div className="mt-0.5">
                            Tracking: <span className="font-mono">{shippo.tracking_number || "—"}</span>
                          </div>
                          {trackingStatus && (
                            <div className="mt-0.5 text-emerald-700 font-semibold">
                              {trackingStatus}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-brand-muted">
                          <Truck size={13} weight="regular" /> Awaiting label
                        </div>
                      )}
                    </div>
                    <div className="md:col-span-1 text-right">
                      <Link
                        to={`/admin/orders/${o.id}`}
                        data-testid={`admin-order-open-${o.id}`}
                        className="inline-flex items-center gap-1 h-9 px-3 rounded-[10px] border border-brand-navy bg-brand-navy text-white text-xs font-semibold hover:bg-brand-navy2 transition-colors"
                      >
                        Open <ArrowRight size={12} weight="bold" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </AdminGuard>
  );
}


// ─── Fulfillment Health panel ────────────────────────────────────────────
//
// Live view of orphaned Stripe sessions, duplicate-order conflicts,
// missing addresses, and pending labels/emails. The "Preview reconcile"
// controls are dry-run only — no wet-run mutations from this UI.

function HealthCounter({ label, value, tone, testid, icon: Icon }) {
  const tones = {
    critical: "bg-red-50 border-red-200 text-red-800",
    warning:  "bg-amber-50 border-amber-200 text-amber-900",
    info:     "bg-brand-surface border-brand-border text-brand-navy",
    ok:       "bg-emerald-50 border-emerald-200 text-emerald-900",
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-[14px] border px-4 py-3 flex items-center gap-3 ${tones[tone] || tones.info}`}
    >
      <div className="shrink-0">
        <Icon size={20} weight="duotone" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">
          {label}
        </div>
        <div className="font-display text-2xl font-semibold">
          {value}
        </div>
      </div>
    </div>
  );
}

function ConflictRow({ item, testid }) {
  return (
    <div
      data-testid={testid}
      className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center py-3 text-sm"
    >
      <div className="md:col-span-6 font-mono text-xs break-all text-red-900">
        {item.session_id}
      </div>
      <div className="md:col-span-3 text-xs text-red-900">
        <span className="font-mono">{item.count}× rows</span>
      </div>
      <div className="md:col-span-3 text-right">
        <Link
          to={`/admin/orders/${item.canonical_order_id || (item.duplicate_order_ids || [])[0]}`}
          data-testid={`health-open-conflict-${item.session_id}`}
          className="inline-flex items-center gap-1 h-8 px-3 rounded-[10px] border border-red-700 bg-white text-red-800 text-[11px] font-semibold hover:bg-red-700 hover:text-white transition-colors"
        >
          Preview <ArrowRight size={11} weight="bold" />
        </Link>
      </div>
    </div>
  );
}

function OrphanRow({ item, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex items-center justify-between gap-3 py-3 text-sm"
    >
      <div className="min-w-0">
        <div className="font-mono text-xs break-all text-amber-900">
          {item.session_id}
        </div>
        <div className="text-[11px] text-brand-muted mt-0.5">
          Paid on {item.created_at ? String(item.created_at).slice(0, 19).replace("T", " ") : "—"}
        </div>
      </div>
      <div className="shrink-0 text-[11px] text-brand-muted">
        no order row
      </div>
    </div>
  );
}

function MissingAddressRow({ item, testid }) {
  return (
    <div
      data-testid={testid}
      className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center py-3 text-sm"
    >
      <div className="md:col-span-7 font-mono text-xs break-all text-amber-900">
        {item.order_id}
      </div>
      <div className="md:col-span-3 text-[11px] text-amber-900">
        missing: <span className="font-mono">{(item.missing_fields || []).join(", ")}</span>
      </div>
      <div className="md:col-span-2 text-right">
        <Link
          to={`/admin/orders/${item.order_id}`}
          data-testid={`health-open-missing-${item.order_id}`}
          className="inline-flex items-center gap-1 h-8 px-3 rounded-[10px] border border-amber-700 bg-white text-amber-800 text-[11px] font-semibold hover:bg-amber-700 hover:text-white transition-colors"
        >
          Preview <ArrowRight size={11} weight="bold" />
        </Link>
      </div>
    </div>
  );
}

function FulfillmentHealthPanel({ health, config, loading, onRefresh }) {
  const counts = health?.counts || {};
  const cfg = config || {};
  const anyIssue =
    (counts.duplicate_conflict || 0) +
    (counts.orphan_no_order || 0) +
    (counts.missing_address || 0) +
    (counts.needs_label || 0) +
    (counts.needs_email || 0) > 0;

  return (
    <div
      className="mps-card p-6 lg:p-7 mb-6"
      data-testid="fulfillment-health-panel"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <ShieldWarning size={18} weight="duotone" className={anyIssue ? "text-red-700" : "text-emerald-700"} />
          <div>
            <div className="overline">Fulfillment health</div>
            <div className="text-[11px] text-brand-muted">
              Every paid Stripe session is tracked here until finalised.
              Recovery is <span className="font-semibold text-brand-navy">preview / dry-run only</span> from this panel.
            </div>
          </div>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          data-testid="fulfillment-health-refresh"
          className="h-9 px-3 rounded-[10px] border border-brand-border bg-white text-brand-navy text-xs font-semibold hover:bg-brand-surface disabled:opacity-50 transition-colors"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <HealthCounter
          label="Duplicate conflicts"
          value={counts.duplicate_conflict ?? "—"}
          tone={(counts.duplicate_conflict || 0) > 0 ? "critical" : "ok"}
          testid="health-count-duplicate"
          icon={Copy}
        />
        <HealthCounter
          label="Missing address"
          value={counts.missing_address ?? "—"}
          tone={(counts.missing_address || 0) > 0 ? "critical" : "ok"}
          testid="health-count-missing-address"
          icon={MapPinLine}
        />
        <HealthCounter
          label="Orphans (paid, no order)"
          value={counts.orphan_no_order ?? "—"}
          tone={(counts.orphan_no_order || 0) > 0 ? "warning" : "ok"}
          testid="health-count-orphans"
          icon={Warning}
        />
        <HealthCounter
          label="Needs label"
          value={counts.needs_label ?? "—"}
          tone={(counts.needs_label || 0) > 0 ? "warning" : "ok"}
          testid="health-count-needs-label"
          icon={Truck}
        />
        <HealthCounter
          label="Needs email"
          value={counts.needs_email ?? "—"}
          tone={(counts.needs_email || 0) > 0 ? "warning" : "ok"}
          testid="health-count-needs-email"
          icon={EnvelopeOpen}
        />
      </div>

      {config && (
        <div
          className="mt-4 rounded-[10px] border border-brand-border bg-brand-surface/60 px-3 py-2 text-[11px] font-mono text-brand-muted flex flex-wrap gap-x-4 gap-y-1"
          data-testid="fulfillment-health-config"
        >
          <span>stripe: <span className={cfg.stripe_webhook_secret_configured ? "text-emerald-700" : "text-red-700"}>{cfg.stripe_mode || "?"} · webhook {cfg.stripe_webhook_secret_configured ? "ok" : "missing"}</span></span>
          <span>shippo: <span className={cfg.shippo_token_configured ? "text-emerald-700" : "text-red-700"}>{cfg.shippo_token_configured ? "ok" : "missing"}</span> · live {String(cfg.shippo_live_label_purchase)}</span>
          <span>resend: <span className={cfg.resend_configured ? "text-emerald-700" : "text-red-700"}>{cfg.resend_configured ? "ok" : "missing"}</span></span>
          <span>from: {cfg.transactional_from_email || "—"}</span>
          <span>admin: {cfg.admin_notification_email || "—"}</span>
        </div>
      )}

      {(health?.duplicate_conflict || []).length > 0 && (
        <div
          className="mt-4 rounded-[12px] border border-red-200 bg-red-50 p-4"
          data-testid="fulfillment-health-conflicts"
        >
          <div className="text-xs font-semibold uppercase tracking-widest text-red-800 flex items-center gap-2 mb-2">
            <ShieldWarning size={14} weight="bold" />
            {health.duplicate_conflict.length} duplicate-order conflict{health.duplicate_conflict.length === 1 ? "" : "s"} — fulfillment halted
          </div>
          <div className="divide-y divide-red-200">
            {health.duplicate_conflict.map((c) => (
              <ConflictRow
                key={c.session_id}
                item={c}
                testid={`conflict-row-${c.session_id}`}
              />
            ))}
          </div>
        </div>
      )}

      {(health?.missing_address || []).length > 0 && (
        <div
          className="mt-4 rounded-[12px] border border-amber-200 bg-amber-50 p-4"
          data-testid="fulfillment-health-missing-addr"
        >
          <div className="text-xs font-semibold uppercase tracking-widest text-amber-900 flex items-center gap-2 mb-2">
            <MapPinLine size={14} weight="bold" />
            {health.missing_address.length} order{health.missing_address.length === 1 ? "" : "s"} missing shipping details
          </div>
          <div className="divide-y divide-amber-200">
            {health.missing_address.map((m) => (
              <MissingAddressRow
                key={m.order_id}
                item={m}
                testid={`missing-row-${m.order_id}`}
              />
            ))}
          </div>
        </div>
      )}

      {(health?.orphan_no_order || []).length > 0 && (
        <div
          className="mt-4 rounded-[12px] border border-amber-200 bg-amber-50 p-4"
          data-testid="fulfillment-health-orphans"
        >
          <div className="text-xs font-semibold uppercase tracking-widest text-amber-900 flex items-center gap-2 mb-2">
            <Warning size={14} weight="bold" />
            {health.orphan_no_order.length} paid Stripe session{health.orphan_no_order.length === 1 ? "" : "s"} without an order row
          </div>
          <div className="divide-y divide-amber-200">
            {health.orphan_no_order.map((o) => (
              <OrphanRow
                key={o.session_id}
                item={o}
                testid={`orphan-row-${o.session_id}`}
              />
            ))}
          </div>
        </div>
      )}

      {!loading && !anyIssue && (
        <div
          className="mt-4 rounded-[12px] border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-900 font-mono"
          data-testid="fulfillment-health-clean"
        >
          All paid Stripe sessions finalised — no orphans, duplicates, or blocked fulfillment.
        </div>
      )}
    </div>
  );
}
